// Forward-only routing workflows (ICT-4 inbox routing) — MATERIAL_PDF,
// JOB_CHANGE (covers both date-change and distribution-method-change
// emails, which already share one classifier category and one destination),
// BILLING_VERIFICATION, and CREDIT_HOLD. These four are all the exact same
// shape — ACE proposes a destination with a one-line reason, the human
// approves ONCE, nothing is calculated or drafted — so rather than four
// near-duplicate files, one factory (makeRouteWorkflow) is instantiated four
// times below with just the category-specific bits that actually differ.
//
// The classifier (utils/proxyEmailClassifier.ts) already computed exactly
// the "one-line reason" (why) and "destination" (routeTo) copy this needs —
// reused verbatim, not reinvented.
//
// REPORT_DROP (RED_REPORT) is deliberately NOT registered here: every Red
// Report email is a portfolio-wide rollup with no relatedJobId at all (see
// data/mockProxyEmails.ts), and this entire registry — buildTasks, the
// Intake Queue table, the item-detail panel — is built around one task per
// ProxyJob. A job-less "task" has nowhere honest to log an approval (every
// ProxyFollowUpRecord lives inside a specific job's followUpHistory). Red
// Reports are filing-only with zero destination choice to review, so they're
// surfaced instead on the All Emails audit view (ProxyAllEmailsPage) as
// already-filed — see that page for the fuller reasoning.
import type { ProxyJob } from '../mockProxyJobs';
import type { ProxyInboxEmail } from '../mockProxyEmails';
import { classifyEmail, type ProxyEmailCategory } from '../../utils/proxyEmailClassifier';
import type {
  ProxyTask, ProxyWorkflowContext, ProxyWorkflowDefinition, ProxyWorkflowType,
  ProxyExtractedField, ProxyReviewPanelSchema, ProxyPanelContext,
} from '../proxyWorkflowTypes';

interface RouteWorkflowConfig {
  type: ProxyWorkflowType;
  label: string;
  noun: string;
  category: ProxyEmailCategory;
  followUpType: string;
  destinationEmail: string;
  ccEmails?: string[];
  // What the routed email changes about the job, once a person approves it.
  onApprove?: (job: ProxyJob, email: ProxyInboxEmail) => Partial<ProxyJob> | void;
}

function buildRouteTask(job: ProxyJob, email: ProxyInboxEmail, config: RouteWorkflowConfig): ProxyTask {
  const cls = classifyEmail(email);
  return {
    id: `route-${config.type.toLowerCase()}-${email.id}`,
    workflowType: config.type,
    jobId: job.jobId,
    title: `${job.issuer} - ${config.label}`,
    subtitle: `Job ${job.jobId}`,
    reason: cls.why,
    source: 'email',
    sourceEmailId: email.id,
    createdAt: email.date,
    // Rule-based classification, never an LLM call — flat, honest confidence
    // per utils/proxyEmailClassifier.ts, same as every other email-sourced task.
    confidence: email.aceConfidence ?? Math.round(cls.confidence * 100),
  };
}

// Done once Ace has logged the routing for this particular email, so a
// second email of the same kind on a job is still routed.
function routeResolved(job: ProxyJob, followUpType: string, email: ProxyInboxEmail): boolean {
  return job.followUpHistory.some(r => r.type === followUpType && r.sourceEmailId === email.id);
}

function makeRouteWorkflow(config: RouteWorkflowConfig): ProxyWorkflowDefinition {
  function buildTasks({ jobs, emails }: ProxyWorkflowContext): ProxyTask[] {
    const tasks: ProxyTask[] = [];
    for (const email of emails) {
      if (!email.relatedJobId) continue;
      if (classifyEmail(email).category !== config.category) continue;
      const job = jobs.find(j => j.jobId === email.relatedJobId);
      if (!job || routeResolved(job, config.followUpType, email)) continue;
      tasks.push(buildRouteTask(job, email, config));
    }
    return tasks;
  }

  function defaultEdits(): Record<string, any> {
    return {}; // nothing to edit — the decision is the destination, not a value
  }

  function buildPanel({ task, job, email }: ProxyPanelContext): ProxyReviewPanelSchema {
    // Always email-sourced by construction (buildTasks only ever creates
    // these from an email) — ProxyTaskPanel resolves it before calling here.
    const src = email!;
    const cls = classifyEmail(src);
    // ICT-4: forward "unless they're already cc'd" — only to the desks missing.
    const alreadyCopied = new Set([src.to, ...src.cc]);
    const recipients = [config.destinationEmail, ...(config.ccEmails || [])];
    const missing = recipients.filter(r => !alreadyCopied.has(r));
    const copied = recipients.filter(r => alreadyCopied.has(r));
    const extractedFields: ProxyExtractedField[] = [
      { key: 'issuer', label: 'Issuer', display: job.issuer },
      { key: 'jobId', label: 'Job #', display: job.jobId },
      { key: 'cusip', label: 'CUSIP', display: job.cusip },
      { key: 'from', label: 'From', display: src.from },
    ];
    return {
      extractedFields,
      calculation: {
        title: 'Routing decision',
        ruleText: cls.why,
        rows: [
          { label: 'Destination', value: cls.routeTo },
          ...(copied.length ? [{ label: 'Already copied', value: copied.join(', ') }] : []),
          ...(cls.fileIn ? [{ label: 'File in', value: cls.fileIn }] : []),
        ],
        totalRow: missing.length
          ? { label: 'Action', value: `Forward to ${missing.join(', ')}` }
          : { label: 'Action', value: 'File only, since everyone who needs it is already copied', tone: 'muted' },
      },
      drafts: [],
      approveSummary: [
        missing.length ? `Forward to ${missing.join(', ')}` : 'No forward needed, already copied',
        ...(cls.fileIn ? [`File the email in ${cls.fileIn}`] : []),
        'Log the routing decision',
        'Move this task to Done.',
      ],
      approveConfirmation: missing.length
        ? `Forwarded to <strong>${missing.join(', ')}</strong> for Job ${job.jobId}.`
        : `Filed for Job ${job.jobId}. Everyone who needs it was already copied.`,
      closesTicket: true,
      outboundActions: [{
        key: 'route', followUpType: config.followUpType,
        // Nothing to forward → the record is just the MIC log of the filing.
        recipientEmail: missing[0] ?? 'mic@betanxt.com', ccEmails: missing.slice(1),
        subject: `Routed: ${task.title}`, bodyPreview: cls.why,
      }],
    };
  }

  return {
    type: config.type,
    label: config.label,
    noun: config.noun,
    trigger: `An inbound email classifies as ${config.category}. No processing needed, just routing.`,
    source: 'Inbound email (classified by utils/proxyEmailClassifier.ts).',
    steps: ['Source & extraction', 'Routing decision', 'Approve'],
    buildTasks,
    defaultEdits,
    buildPanel,
    onApprove: config.onApprove ? ({ job, email }) => config.onApprove!(job, email!) : undefined,
  };
}

export const MATERIAL_PDF_ROUTE_WORKFLOW = makeRouteWorkflow({
  type: 'MATERIAL_PDF_ROUTE', label: 'Material PDF Routing', noun: 'a material PDF routing',
  category: 'MATERIAL_PDF', followUpType: 'routed_material_pdf',
  destinationEmail: 'documents@betanxt.com', ccEmails: ['verifications@betanxt.com', 'notifications@betanxt.com'],
});

export const JOB_CHANGE_ROUTE_WORKFLOW = makeRouteWorkflow({
  type: 'JOB_CHANGE_ROUTE', label: 'Job Change Routing', noun: 'a job change routing',
  category: 'JOB_CHANGE', followUpType: 'routed_job_change',
  // ICT-4: date and distribution-method changes go to all three desks.
  destinationEmail: 'documents@betanxt.com', ccEmails: ['verifications@betanxt.com', 'notifications@betanxt.com'],
  // Electronic only means no printed sets to chase; a new meeting date means
  // holding the chase until Discovery has updated the job.
  onApprove: (_job, email) => {
    const text = `${email.subject} ${email.body}`.toLowerCase();
    if (/electronic(ally)? only|electronic distribution only/.test(text)) return { econsentOnly: true };
    if (/meeting date|rescheduled|postpone/.test(text)) return { heldForDiscovery: 'Meeting dates changing' };
  },
});

export const BILLING_VERIFICATION_ROUTE_WORKFLOW = makeRouteWorkflow({
  type: 'BILLING_VERIFICATION_ROUTE', label: 'Billing Verification Routing', noun: 'a billing verification routing',
  category: 'BILLING_VERIFICATION', followUpType: 'routed_billing_verification',
  destinationEmail: 'ict-billing@betanxt.com',
});

export const CREDIT_HOLD_ROUTE_WORKFLOW = makeRouteWorkflow({
  type: 'CREDIT_HOLD_ROUTE', label: 'Credit Hold Routing', noun: 'a credit hold routing',
  category: 'CREDIT_HOLD', followUpType: 'routed_credit_hold',
  destinationEmail: 'ict-credit@betanxt.com',
  onApprove: () => ({ creditHold: true }),
});
